import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import { observer } from "mobx-react-lite";
import TimelineAssetItem from "./TimelineCalendarParts/TimelineAssetItem";
import {
  getTimelineDateKey,
  getTimelineWeekEnd,
  getTimelineWeekStart,
  normalizeTimelineDate,
  TIMELINE_DATE_COLUMN_WIDTH,
  TimelineContext,
} from "../../../../../contexts/TimelineContext";
import { SharedCampaignContext } from "../../../../../contexts/Advertiser/SharedCampaignContext";
import axiosService from "../../../../../utils/AxiosService";
import {
  format,
  eachDayOfInterval,
  isBefore,
  isAfter,
  min as minDate,
  max as maxDate,
} from "date-fns";
import {
  getTimelineDayState,
  getTimelineItemVisualWidth,
  groupTimelineDatesByWeek,
  isTimelineDateOutsideRange,
  TimelineDateHeader,
  TimelineShowTodayButton,
  timelineDayBackgroundClass,
} from "../../../../Shared/TimelineParts/TimelineCalendarUi";

const DATE_COLUMN_WIDTH = TIMELINE_DATE_COLUMN_WIDTH;

const TimelineReadonlyBlock = observer(function TimelineReadonlyBlock({
  asset,
  rowIndex,
  visualWidth,
}) {
  const [isHovering, setIsHovering] = useState(false);

  const expandedWidth = isHovering ? "fit-content" : `${visualWidth}px`;

  const onMouseOver = (e) => {
    const primaryLabel = e.currentTarget.querySelector(
      "[data-timeline-primary-label]",
    );
    if (primaryLabel && primaryLabel.scrollWidth > primaryLabel.clientWidth) {
      setIsHovering(true);
    }
  };

  return (
    <div
      className="absolute"
      style={{
        top: `${rowIndex * 40}px`,
        left: 0,
        width: `${DATE_COLUMN_WIDTH}px`,
        height: "32px",
        zIndex: 10,
      }}
    >
      <div
        onMouseOver={onMouseOver}
        onMouseLeave={() => setIsHovering(false)}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: expandedWidth,
          minWidth: `${visualWidth}px`,
          height: "32px",
        }}
      >
        <TimelineAssetItem timelineAsset={asset} />
      </div>
    </div>
  );
});

export default observer(function TimelineCalendar() {
  const { campaign } = useContext(SharedCampaignContext);
  const {
    timelineState,
    jumpToDate,
    layout,
    campaignScenario,
    clearJumpToDate,
  } = useContext(TimelineContext);

  const [dates, setDates] = useState([]);
  const [groupedDates, setGroupedDates] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [allItems, setAllItems] = useState([]);
  const [germanHolidayByDate, setGermanHolidayByDate] = useState({});
  const minRows = 10;

  const scrollContainerRef = useRef(null);
  const now = new Date();
  const campaignStartDate = normalizeTimelineDate(campaign?.start_date);
  const campaignEndDate = normalizeTimelineDate(campaign?.end_date);

  const { displayStartDate, displayEndDate } = useMemo(() => {
    const start = normalizeTimelineDate(campaign?.start_date);
    const end = normalizeTimelineDate(campaign?.end_date);

    const dayCandidates = [];
    if (start) dayCandidates.push(start);
    if (end) dayCandidates.push(end);

    const pushPlacement = (startVal, endVal) => {
      const s = normalizeTimelineDate(startVal);
      const e = normalizeTimelineDate(endVal || startVal);
      if (s) dayCandidates.push(s);
      if (e) dayCandidates.push(e);
    };

    (campaignScenario?.actions || []).forEach((a) =>
      pushPlacement(a.start_date, a.end_date),
    );
    (campaignScenario?.campaign_scenarios_project_assets || []).forEach((a) =>
      pushPlacement(a.start_date, a.end_date),
    );

    if (!dayCandidates.length) {
      return { displayStartDate: null, displayEndDate: null };
    }

    return {
      displayStartDate: minDate(dayCandidates),
      displayEndDate: maxDate(dayCandidates),
    };
  }, [
    campaign?.start_date,
    campaign?.end_date,
    campaignScenario?.id,
    (campaignScenario?.actions || [])
      .map((a) => `${a.id}:${a.start_date}:${a.end_date}`)
      .join("|"),
    (campaignScenario?.campaign_scenarios_project_assets || [])
      .map((a) => `${a.id}:${a.start_date}:${a.end_date}`)
      .join("|"),
  ]);

  const clampToDisplayRange = (value) => {
    const parsedDate = normalizeTimelineDate(value);
    if (!parsedDate) return displayStartDate || now;
    if (displayStartDate && isBefore(parsedDate, displayStartDate))
      return displayStartDate;
    if (displayEndDate && isAfter(parsedDate, displayEndDate))
      return displayEndDate;
    return parsedDate;
  };

  const generateDateRange = (startDate, endDate) => {
    setDates(
      eachDayOfInterval({
        start: new Date(startDate),
        end: new Date(endDate),
      }),
    );
  };

  const visibleYearsKey = useMemo(() => {
    if (!dates.length) return "";
    const years = [...new Set(dates.map((d) => format(d, "yyyy")))].sort();
    return years.join(",");
  }, [dates]);

  useEffect(() => {
    if (!visibleYearsKey) return;

    axiosService
      .get("/api/advertiser/campaign_share/german_public_holidays", {
        params: { years: visibleYearsKey },
      })
      .then((body) => {
        if (body?.holidays) {
          setGermanHolidayByDate((prev) => ({ ...prev, ...body.holidays }));
        }
      })
      .catch(() => {});
  }, [visibleYearsKey]);

  const germanHolidayName = (date) =>
    germanHolidayByDate[getTimelineDateKey(date)];

  const navigateToStartDate = (startDate) => {
    if (scrollContainerRef.current && dates.length > 0) {
      const clampedDate = clampToDisplayRange(startDate);
      const dateStr = getTimelineDateKey(clampedDate);
      const dateIndex = dates.findIndex(
        (d) => getTimelineDateKey(d) === dateStr,
      );

      if (dateIndex !== -1) {
        const rawScrollPosition =
          dateIndex * DATE_COLUMN_WIDTH -
          scrollContainerRef.current.clientWidth / 2 +
          DATE_COLUMN_WIDTH / 2;
        const maxScrollPosition = Math.max(
          0,
          scrollContainerRef.current.scrollWidth -
            scrollContainerRef.current.clientWidth,
        );
        const scrollPosition = Math.min(
          Math.max(rawScrollPosition, 0),
          maxScrollPosition,
        );
        scrollContainerRef.current.scrollLeft = scrollPosition;
      }
    }
  };

  useEffect(() => {
    if (!jumpToDate || dates.length === 0) return;
    try {
      const clampedJumpDate = clampToDisplayRange(jumpToDate);
      if (!clampedJumpDate) return;
      const frame = requestAnimationFrame(() => {
        navigateToStartDate(clampedJumpDate);
        clearJumpToDate();
      });
      return () => cancelAnimationFrame(frame);
    } catch {
      clearJumpToDate();
    }
  }, [jumpToDate, dates.length]);

  useEffect(() => {
    if (displayStartDate && displayEndDate) {
      generateDateRange(
        getTimelineWeekStart(displayStartDate),
        getTimelineWeekEnd(displayEndDate),
      );
      return;
    }

    const fallbackNow = new Date();
    const fallbackStart = getTimelineWeekStart(
      new Date(
        fallbackNow.getFullYear(),
        fallbackNow.getMonth(),
        fallbackNow.getDate() - 14,
      ),
    );
    const fallbackEnd = getTimelineWeekEnd(
      new Date(
        fallbackNow.getFullYear(),
        fallbackNow.getMonth(),
        fallbackNow.getDate() + 28,
      ),
    );
    generateDateRange(fallbackStart, fallbackEnd);
  }, [displayStartDate, displayEndDate]);

  useEffect(() => setGroupedDates(groupTimelineDatesByWeek(dates)), [dates]);

  useEffect(() => {
    setIsInitialLoad(true);
  }, [displayStartDate, displayEndDate]);

  useEffect(() => {
    if (
      scrollContainerRef.current &&
      groupedDates.length > 0 &&
      isInitialLoad
    ) {
      const todayInRange = clampToDisplayRange(new Date());
      navigateToStartDate(todayInRange);
      setIsInitialLoad(false);
    }
  }, [groupedDates, isInitialLoad, displayStartDate, displayEndDate]);

  useEffect(() => {
    const items = new Set();
    dates.forEach((date) => {
      const dateStr = getTimelineDateKey(date);
      timelineState.getItemsForDate(dateStr).forEach((item) => {
        items.add(item.id);
      });
    });
    setAllItems(Array.from(items));
  }, [dates, timelineState.timelineItems]);

  const rowCount = Math.max(allItems.length, minRows);

  const weekBlocks = groupedDates.map((weekDates) => (
    <div
      key={`week-${format(weekDates[0], "yyyy-MM-dd")}`}
      className="flex h-full flex-col"
    >
      <div
        className="grid border-b border-r-1 border-rb-widget-border bg-white dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode"
        style={{
          gridTemplateColumns: `repeat(${weekDates.length}, minmax(0, ${DATE_COLUMN_WIDTH}px))`,
        }}
      >
        {groupTimelineDatesByWeek(weekDates).map((innerWeek, weekIndex) => {
          const weekStart = getTimelineWeekStart(innerWeek[0]);
          const weekEnd = getTimelineWeekEnd(innerWeek[0]);

          return (
            <div
              key={weekIndex}
              className="whitespace-nowrap p-2 text-center font-semibold dark:text-white"
              style={{
                gridColumn: `span ${innerWeek.length} / span ${innerWeek.length}`,
              }}
            >
              {format(weekStart, "MMM d")} - {format(weekEnd, "MMM d")},{" "}
              {format(weekEnd, "yyyy")}
            </div>
          );
        })}
      </div>

      <div
        className="grid border-b border-rb-widget-border bg-white dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode"
        style={{
          gridTemplateColumns: `repeat(${weekDates.length}, minmax(0, ${DATE_COLUMN_WIDTH}px))`,
        }}
      >
        {weekDates.map((date, index) => {
          const holidayName = germanHolidayName(date);
          const isOutsideTimelineRange = isTimelineDateOutsideRange(
            date,
            displayStartDate,
            displayEndDate,
          );
          const dayState = getTimelineDayState(date, holidayName, {
            isOutsideTimelineRange,
          });

          return (
            <div
              key={index}
              title={holidayName || undefined}
              className={`border-r border-rb-widget-border text-center dark:border-rb-dark-mode-lighter ${timelineDayBackgroundClass(dayState)}`}
            >
              <TimelineDateHeader
                date={date}
                holidayName={holidayName}
                isOutsideTimelineRange={isOutsideTimelineRange}
              />
            </div>
          );
        })}
      </div>

      <div className="relative flex-grow">
        <div
          className="grid h-full bg-white dark:bg-rb-dark-mode"
          style={{
            gridTemplateColumns: `repeat(${weekDates.length}, minmax(0, ${DATE_COLUMN_WIDTH}px))`,
            position: "relative",
            minHeight: `${rowCount * 40}px`,
          }}
        >
          {weekDates.map((date) => {
            const dateStr = getTimelineDateKey(date);
            const itemsForDate = timelineState.getItemsForDate(dateStr);
            const holidayName = germanHolidayName(date);
            const dayColumnClass = timelineDayBackgroundClass(
              getTimelineDayState(date, holidayName, {
                isOutsideTimelineRange: isTimelineDateOutsideRange(
                  date,
                  displayStartDate,
                  displayEndDate,
                ),
              }),
            );

            return (
              <div
                key={`date-${dateStr}`}
                title={holidayName || undefined}
                className={`relative h-full border-r border-rb-widget-border dark:border-rb-dark-mode-lighter ${dayColumnClass}`}
                style={{
                  minWidth: `${DATE_COLUMN_WIDTH}px`,
                  minHeight: "40px",
                }}
              >
                {itemsForDate.map((asset) => {
                  const visualWidth = getTimelineItemVisualWidth(
                    asset,
                    timelineState,
                  );
                  return (
                    <TimelineReadonlyBlock
                      key={`timeline-${asset.id}`}
                      asset={asset}
                      rowIndex={allItems.indexOf(asset.id)}
                      visualWidth={visualWidth}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  ));

  return (
    <div className="relative h-full min-h-0 flex-1">
      <div
        ref={scrollContainerRef}
        className="h-full overflow-x-auto bg-rb-grey dark:bg-rb-dark-mode"
      >
        <div className="flex h-full min-w-max">{weekBlocks}</div>
      </div>
      <TimelineShowTodayButton
        onClick={() => navigateToStartDate(new Date())}
        className="absolute bottom-4 right-4 z-30"
        startDate={campaignStartDate}
        endDate={campaignEndDate}
      />
    </div>
  );
});
